import { Money } from '../ui/Money';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Plus, Trash2, Check, Calendar, DollarSign, 
  CreditCard, ChevronDown, ChevronUp, Edit2, X, Download, Receipt
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { format } from 'date-fns';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdvancesSection = ({ employee, onUpdate, isViewer = false }) => {
  const [advances, setAdvances] = useState([]);
  const [advanceTypes, setAdvanceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAddTypeDialog, setShowAddTypeDialog] = useState(false);
  const [expandedAdvance, setExpandedAdvance] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [advanceToDelete, setAdvanceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    advance_type: '',
    amount: '',
    date_requested: new Date().toISOString().split('T')[0],
    reason: '',
    notes: '',
    deduction_mode: 'payroll',
    installments: []
  });
  const [instCount, setInstCount] = useState('');
  const [instStart, setInstStart] = useState('');

  useEffect(() => {
    if (employee?.id) {
      loadData();
    }
  }, [employee?.id]);

  const loadData = async () => {
    try {
      const [advancesRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/api/advances/employee/${employee.id}`),
        axios.get(`${API_URL}/api/advances/types`)
      ]);
      setAdvances(advancesRes.data);
      setAdvanceTypes(typesRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load advances', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstallment = () => {
    setFormData(prev => ({
      ...prev,
      installments: [...prev.installments, { amount: '', due_date: '', notes: '' }]
    }));
  };

  // Auto-generate an even monthly repayment schedule. Splits the total across
  // `instCount` months; the FINAL installment absorbs any rounding remainder.
  const generateSchedule = () => {
    const total = parseFloat(formData.amount);
    const n = parseInt(instCount, 10);
    if (!total || total <= 0) {
      toast({ title: 'Enter the advance amount first', variant: 'destructive' });
      return;
    }
    if (!n || n <= 0) {
      toast({ title: 'Enter a valid number of installments', variant: 'destructive' });
      return;
    }
    if (!instStart) {
      toast({ title: 'Pick the first installment month', variant: 'destructive' });
      return;
    }
    const base = Math.floor((total / n) * 100) / 100;
    const last = Math.round((total - base * (n - 1)) * 100) / 100;
    const start = new Date(instStart + 'T00:00:00');
    const rows = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, start.getDate());
      const due = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      rows.push({ amount: String(i === n - 1 ? last : base), due_date: due, notes: '' });
    }
    setFormData(prev => ({ ...prev, installments: rows }));
    toast({ title: `${n} installments generated`, description: `Final month: AED ${last.toLocaleString()} (absorbs rounding)` });
  };

  const installmentsTotal = formData.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const amountNum = parseFloat(formData.amount) || 0;
  const installmentsMismatch = formData.installments.length > 0 && Math.abs(installmentsTotal - amountNum) > 0.01;

  const handleRemoveInstallment = (index) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index)
    }));
  };

  const handleInstallmentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.map((inst, i) => 
        i === index ? { ...inst, [field]: value } : inst
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.advance_type || !formData.amount) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    try {
      const payload = {
        employee_id: employee.id,
        advance_type: formData.advance_type,
        amount: parseFloat(formData.amount),
        date_requested: formData.date_requested,
        reason: formData.reason,
        notes: formData.notes,
        deduction_mode: formData.deduction_mode,
        installments: formData.installments
          .filter(i => i.amount && i.due_date)
          .map(i => ({
            amount: parseFloat(i.amount),
            due_date: i.due_date,
            notes: i.notes || ''
          }))
      };

      await axios.post(`${API_URL}/api/advances`, payload);
      toast({ title: 'Success', description: 'Advance added successfully' });
      
      setFormData({
        advance_type: '',
        amount: '',
        date_requested: new Date().toISOString().split('T')[0],
        reason: '',
        notes: '',
        deduction_mode: 'payroll',
        installments: []
      });
      setInstCount('');
      setInstStart('');
      setShowAddDialog(false);
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add advance', variant: 'destructive' });
    }
  };

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) {
      toast({ title: 'Error', description: 'Please enter a type name', variant: 'destructive' });
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/advances/types?name=${encodeURIComponent(newTypeName)}`);
      setAdvanceTypes(prev => [...prev, response.data]);
      setFormData(prev => ({ ...prev, advance_type: response.data.id }));
      setNewTypeName('');
      setShowAddTypeDialog(false);
      toast({ title: 'Success', description: 'Advance type added' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add type', variant: 'destructive' });
    }
  };

  const handleDownloadInstallmentReceipt = async (advanceId, installmentId) => {
    try {
      const response = await fetch(`${API_URL}/api/advances/${advanceId}/installments/${installmentId}/receipt`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payment_Receipt_${employee.employee_code}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate payment receipt', variant: 'destructive' });
    }
  };

  const handleMarkInstallmentPaid = async (advanceId, installmentId) => {
    try {
      await axios.post(`${API_URL}/api/advances/${advanceId}/installments/${installmentId}/mark-paid`);
      toast({ title: 'Installment marked as paid', description: 'Downloading payment receipt…' });
      loadData();
      // Print the payment receipt for this installment right away
      handleDownloadInstallmentReceipt(advanceId, installmentId);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update installment', variant: 'destructive' });
    }
  };

  const handleDeleteAdvance = async (advanceId, advanceType) => {
    setAdvanceToDelete({ id: advanceId, type: advanceType });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAdvance = async () => {
    if (!advanceToDelete) return;
    setDeleting(true);
    
    try {
      await axios.delete(`${API_URL}/api/advances/${advanceToDelete.id}`);
      toast({ title: 'Success', description: 'Advance deleted' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete advance', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setAdvanceToDelete(null);
    }
  };

  const handleAddInstallmentToExisting = async (advanceId) => {
    const amount = prompt('Enter installment amount:');
    if (!amount) return;
    
    const dueDate = prompt('Enter due date (YYYY-MM-DD):');
    if (!dueDate) return;

    try {
      await axios.post(`${API_URL}/api/advances/${advanceId}/installments`, {
        amount: parseFloat(amount),
        due_date: dueDate,
        notes: ''
      });
      toast({ title: 'Success', description: 'Installment added' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add installment', variant: 'destructive' });
    }
  };

  const handleDownloadReceipt = async (advanceId) => {
    try {
      toast({ title: 'Preparing receipt…' });
      const response = await fetch(`${API_URL}/api/advances/${advanceId}/receipt`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Advance_Receipt_${employee.employee_code}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Receipt downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate receipt', variant: 'destructive' });
    }
  };

  const handleExport = async (format) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating ${format.toUpperCase()} report...` });
      const response = await fetch(`${API_URL}/api/advances/export/employee/${employee.id}?format=${format}`);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Advances_${employee.employee_code}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Download Complete', description: 'Advance report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download report', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'paid': 'bg-green-500',
      'partially_paid': 'bg-amber-500',
      'approved': 'bg-blue-500',
      'pending': 'bg-gray-500',
      'pending_installments': 'bg-purple-500'
    };
    return <Badge className={styles[status] || 'bg-gray-500'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getTypeName = (typeId) => {
    const type = advanceTypes.find(t => t.id === typeId);
    return type?.name || typeId;
  };

  // Calculate totals
  const totalAdvanced = advances.reduce((sum, a) => sum + (a.amount || 0), 0);
  const totalPaid = advances.reduce((sum, a) => sum + (a.total_paid || 0), 0);
  const totalPending = totalAdvanced - totalPaid;

  if (loading) {
    return <div className="text-center py-4">Loading advances...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p className="text-xs text-blue-600">Total Advanced</p>
          <p className="text-lg font-bold text-blue-700"><Money value={totalAdvanced} /></p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
          <p className="text-xs text-green-600">Total Repaid</p>
          <p className="text-lg font-bold text-green-700"><Money value={totalPaid} /></p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
          <p className="text-xs text-red-600">Outstanding</p>
          <p className="text-lg font-bold text-red-700"><Money value={totalPending} /></p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {!isViewer && (
          <Button onClick={() => setShowAddDialog(true)} className="flex-1" data-testid="add-advance-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add New Advance
          </Button>
        )}
        {advances.length > 0 && (
          <>
            <Button variant="outline" onClick={() => handleExport('pdf')} title="Download PDF">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" onClick={() => handleExport('excel')} title="Download Excel">
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
          </>
        )}
      </div>

      {/* Advances List */}
      <div className="space-y-3">
        {advances.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
            No advance records found
          </div>
        ) : (
          advances.map(advance => (
            <Card key={advance.id} className="border-l-4 border-l-blue-500">
              <CardHeader className="py-3 cursor-pointer" onClick={() => setExpandedAdvance(expandedAdvance === advance.id ? null : advance.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-semibold">{getTypeName(advance.advance_type)}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(advance.date_requested), 'dd MMM yyyy')}
                      </p>
                      {advance.deduction_mode && (
                        <Badge variant="outline" className={`mt-1 text-[10px] ${advance.deduction_mode === 'separate' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}`}>
                          {advance.deduction_mode === 'separate' ? 'Paid separately' : 'Payroll deduction'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-lg"><Money value={advance.amount} /></p>
                      <p className="text-xs text-gray-500">
                        Remaining: <span className="text-red-600 font-medium"><Money value={advance.remaining_balance} /></span>
                      </p>
                    </div>
                    {getStatusBadge(advance.status)}
                    {expandedAdvance === advance.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                </div>
              </CardHeader>
              
              {expandedAdvance === advance.id && (
                <CardContent className="pt-0 border-t">
                  {advance.reason && (
                    <div className="mb-3 p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">Reason</p>
                      <p className="text-sm">{advance.reason}</p>
                    </div>
                  )}
                  
                  {/* Installments */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">Repayment Schedule</p>
                      {!isViewer && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAddInstallmentToExisting(advance.id)}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Installment
                        </Button>
                      )}
                    </div>
                    
                    {advance.installments?.length > 0 ? (
                      <div className="space-y-2">
                        {advance.installments.map((inst, idx) => (
                          <div 
                            key={inst.id} 
                            className={`flex items-center justify-between p-2 rounded border ${
                              inst.status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500">#{idx + 1}</span>
                              <div>
                                <p className="font-medium"><Money value={inst.amount} /></p>
                                <p className="text-xs text-gray-500">
                                  Due: {format(new Date(inst.due_date), 'dd MMM yyyy')}
                                  {inst.paid_date && ` | Paid: ${format(new Date(inst.paid_date), 'dd MMM yyyy')}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {inst.status === 'paid' ? (
                                <>
                                  <Badge className="bg-green-500">Paid</Badge>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => handleDownloadInstallmentReceipt(advance.id, inst.id)}
                                    data-testid={`installment-receipt-${inst.id}`}
                                    title="Print payment receipt"
                                  >
                                    <Receipt className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              ) : (
                                !isViewer && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-green-600 border-green-300"
                                    onClick={() => handleMarkInstallmentPaid(advance.id, inst.id)}
                                  >
                                    <Check className="h-3 w-3 mr-1" /> Mark Paid
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-2">No installments scheduled</p>
                    )}
                  </div>
                  
                  {/* Delete button */}
                  {!isViewer && (
                    <div className="mt-4 pt-3 border-t flex justify-between items-center gap-2">
                      {advance.deduction_mode === 'separate' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-700 border-purple-300 hover:bg-purple-50"
                          onClick={() => handleDownloadReceipt(advance.id)}
                          data-testid={`advance-receipt-${advance.id}`}
                          title="Download repayment receipt for staff"
                        >
                          <Receipt className="h-4 w-4 mr-1" /> Repayment Receipt
                        </Button>
                      ) : <span />}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => handleDeleteAdvance(advance.id, advance.advance_type)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete Advance
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Add Advance Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Add Salary Advance - {employee?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Advance Type */}
            <div>
              <Label>Advance Type *</Label>
              <div className="flex gap-2">
                <Select 
                  value={formData.advance_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, advance_type: v }))}
                >
                  <SelectTrigger className="flex-1" data-testid="advance-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {advanceTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddTypeDialog(true)}
                  title="Add new type"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Amount */}
            <div>
              <Label>Amount (AED) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
                required
                data-testid="advance-amount-input"
              />
            </div>

            {/* Date Requested */}
            <div>
              <Label>Date Requested</Label>
              <Input
                type="date"
                value={formData.date_requested}
                onChange={(e) => setFormData(prev => ({ ...prev, date_requested: e.target.value }))}
              />
            </div>

            {/* Reason */}
            <div>
              <Label>Reason</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason for advance (optional)"
                rows={2}
              />
            </div>

            {/* Deduction Mode */}
            <div>
              <Label>How will this be recovered?</Label>
              <Select
                value={formData.deduction_mode}
                onValueChange={(v) => setFormData(prev => ({ ...prev, deduction_mode: v }))}
              >
                <SelectTrigger data-testid="advance-deduction-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payroll">Deduct from payroll</SelectItem>
                  <SelectItem value="separate">Paid separately (outside payroll)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Installments */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Repayment Installments</Label>
                <Button type="button" size="sm" variant="outline" onClick={handleAddInstallment}>
                  <Plus className="h-3 w-3 mr-1" /> Add Installment
                </Button>
              </div>

              {/* Auto-generate schedule */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="advance-auto-schedule">
                <p className="text-xs font-medium text-blue-700">Auto-calculate schedule</p>
                <div className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[110px]">
                    <Label className="text-xs">No. of Installments</Label>
                    <Input
                      type="number" min="1" step="1"
                      value={instCount}
                      onChange={(e) => setInstCount(e.target.value)}
                      placeholder="e.g., 6"
                      data-testid="advance-inst-count"
                    />
                  </div>
                  <div className="flex-1 min-w-[130px]">
                    <Label className="text-xs">First Installment</Label>
                    <Input
                      type="date"
                      value={instStart}
                      onChange={(e) => setInstStart(e.target.value)}
                      data-testid="advance-inst-start"
                    />
                  </div>
                  <Button type="button" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={generateSchedule} data-testid="advance-generate-schedule">
                    Generate
                  </Button>
                </div>
                <p className="text-[11px] text-blue-600/80">
                  Splits the amount evenly across the months — the final month absorbs any rounding. You can edit any amount below afterwards.
                </p>
              </div>

              {formData.installments.length > 0 && (
                <div className={`text-xs px-3 py-1.5 rounded-md flex items-center justify-between ${installmentsMismatch ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-testid="advance-inst-total">
                  <span>Installments total: <span className="font-semibold">AED {installmentsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  {installmentsMismatch
                    ? <span>≠ Advance AED {amountNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    : <span>✓ matches advance</span>}
                </div>
              )}
              
              {formData.installments.map((inst, index) => (
                <div key={index} className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={inst.amount}
                          onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                          placeholder="Amount"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Due Date</Label>
                        <Input
                          type="date"
                          value={inst.due_date}
                          onChange={(e) => handleInstallmentChange(index, 'due_date', e.target.value)}
                        />
                      </div>
                    </div>
                    <Input
                      value={inst.notes}
                      onChange={(e) => handleInstallmentChange(index, 'notes', e.target.value)}
                      placeholder="Notes (optional)"
                    />
                  </div>
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 mt-5"
                    onClick={() => handleRemoveInstallment(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {formData.installments.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2 bg-gray-50 rounded">
                  No installments added. You can add them later.
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Add Advance
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add New Type Dialog */}
      <Dialog open={showAddTypeDialog} onOpenChange={setShowAddTypeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New Advance Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type Name</Label>
              <Input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="e.g., Medical Advance"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddTypeDialog(false)}>Cancel</Button>
              <Button onClick={handleAddNewType}>Add Type</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Salary Advance"
        description="Are you sure you want to delete this salary advance record? This action cannot be undone and all associated installments will also be removed."
        itemName={advanceToDelete?.type}
        onConfirm={confirmDeleteAdvance}
        loading={deleting}
      />
    </div>
  );
};

export default AdvancesSection;
